import { ApiService, NewValuationAllocationVersion } from 'api';
import { AuxCashTaxRateType } from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/components/EffectiveTaxRateLedger/types';
import { ValuationWithReferences } from 'pages/ValuationsAllocation/types';
import BaseServiceRetryWrapper from './baseRetry';
import { VALUATIONS } from '../common/endpoints';

class ValuationService extends BaseServiceRetryWrapper {
  getValuation = (allocationId: string) => this.get(VALUATIONS.valuationByAllocationId(allocationId));

  createNewVersion = async (valuation: ValuationWithReferences, keep_notes?: boolean): Promise<NewValuationAllocationVersion> => {
    const allocationId = valuation.allocation_id?.toString() ?? '';
    if (!allocationId) {
      throw new Error('Allocation ID is required');
    }

    if (!valuation.allocation) {
      throw new Error('Allocation is required');
    }

    if (!valuation.name) {
      throw new Error('Name is required');
    }

    if (!valuation.market_currency) {
      throw new Error('Market currency is required');
    }

    // Map ValuationWithReferences to NewValuationAllocationVersion
    // Explicitly handle keep_notes: if undefined/null, default to true; if explicitly false, use false
    const newVersionData: NewValuationAllocationVersion = {
      allocation_id: valuation.allocation_id,
      allocation: valuation.allocation as NewValuationAllocationVersion['allocation'],
      valuations_approaches: valuation.valuations_approaches as NewValuationAllocationVersion['valuations_approaches'],
      name: valuation.name,
      keep_notes: keep_notes ?? true,
      market_currency: valuation.market_currency,
      market_date: valuation.market_date ?? null,
      is_firm_owned: valuation.is_firm_owned,
      is_ready_for_audit: valuation.is_ready_for_audit,
      is_deleted: valuation.is_deleted ?? null,
    };

    return ApiService.apiValuationAllocationNewVersionUpdate(allocationId, newVersionData);
  };

  getValuationApproaches = (measurementDateId: string, allocationVersionId: string) =>
    this.get(VALUATIONS.getValuationApproaches(measurementDateId, allocationVersionId));

  updateValuation = (valuation: ValuationWithReferences) =>
    this.put(VALUATIONS.valuationByAllocationId(valuation.allocation_id?.toString() ?? ''), valuation);

  getGpc = (ticker: string, md: number, currency: string, calendarYears: string, customDate?: string) =>
    this.get(VALUATIONS.getGPC(ticker, md, currency, calendarYears, customDate));

  getPublicCompList = (
    tickerList: string[],
    md: string,
    currency: string,
    calendarYears: string,
    customDate: string,
    searchForIndexes = false
  ) => this.get(VALUATIONS.getPublicCompList(md, currency, tickerList.join(','), calendarYears, customDate, searchForIndexes));

  getEffectiveTaxRate = (companyMeasurementDate: string) =>
    this.get(VALUATIONS.getEffectiveTaxRate(companyMeasurementDate));

  saveEffectiveTaxRate = (data: AuxCashTaxRateType) => {
    if (data.id) {
      return this.put(VALUATIONS.updateEffectiveTaxRate(data.id?.toString() ?? ''), data);
    }
    return this.post(VALUATIONS.createEffectiveTaxRate, data);
  };

  getFirmCompGroups = (firmId: number) => this.get(VALUATIONS.getFirmCompGroups(`${firmId}`));

  getTickerByKeyword = (keyword: string) => this.get(VALUATIONS.getTickerByKeyword(keyword));

  getTransaction = (transactionId: string) => this.get(VALUATIONS.getTransaction(transactionId));

  setReadyForAudit = (valuationId: string, data: unknown) => this.put(VALUATIONS.setReadyForAudit(valuationId), data);
}

export default ValuationService;
